import type { IndicatorId } from "../components/IndicatorsDropdown";

// ─── Settings types ───────────────────────────────────────────────────────────

export const MOVING_AVERAGE_TYPES = ["SMA", "WMA", "EMA", "SMMA", "TMA"] as const;
export type MovingAverageType = (typeof MOVING_AVERAGE_TYPES)[number];

/** Applied price for Envelopes (Figma: Source). */
export const ENVELOPE_SOURCES = ["close", "open", "high", "low", "hl2", "hlc3", "ohlc4"] as const;
export type EnvelopeSource = (typeof ENVELOPE_SOURCES)[number];

export const STROKE_WIDTHS = [1, 2, 3, 4, 5] as const;
export type StrokeWidth = (typeof STROKE_WIDTHS)[number];
export type FigureType = "line" | "histogram";

export type LineStyle = {
  visible: boolean;
  color: string;
  width: StrokeWidth;
  opacity: number;
};

export type FillStyle = {
  visible: boolean;
  color: string;
  opacity: number;
};

export type OscillatorStyle = {
  kind: "oscillator";
  main: LineStyle;
  figure: FigureType;
  upColor: string;
  downColor: string;
  /** When false, Figure toggle is hidden (ROC / BB Width). */
  allowFigureSwitch?: boolean;
};

export type HistogramOnlyStyle = {
  kind: "histogram";
  upColor: string;
  downColor: string;
  opacity: number;
  width: StrokeWidth;
};

export type ChannelStyle = {
  kind: "channel";
  top: LineStyle;
  middle: LineStyle;
  bottom: LineStyle;
  background?: FillStyle;
};

export type DeMarkerStyle = {
  kind: "demarker";
  main: LineStyle;
  levelHigh: LineStyle;
  levelLow: LineStyle;
  figure: FigureType;
};

export type DualLineStyle = {
  kind: "dualline";
  up: LineStyle;
  down: LineStyle;
};

export type StcStyle = {
  kind: "stc";
  main: LineStyle;
  levelHigh: LineStyle;
  levelLow: LineStyle;
};

export type MarkerSideStyle = {
  visible: boolean;
  color: string;
};

export type FractalStyle = {
  kind: "fractal";
  up: MarkerSideStyle;
  down: MarkerSideStyle;
};

export type ChaosBandsStyle = {
  kind: "chaosbands";
  up: LineStyle;
  down: LineStyle;
  background: FillStyle;
};

export type IchimokuStyle = {
  kind: "ichimoku";
  conversion: LineStyle;
  base: LineStyle;
  spanA: LineStyle;
  spanB: LineStyle;
  lagging: LineStyle;
  bullishCloud: FillStyle;
  bearishCloud: FillStyle;
};

/** Shared style model for the original indicator set. Keys mirror the
 * series names from the Figma settings panels (main, signal, top, etc.). */
export type LegacyIndicatorStyle = {
  kind: "legacy";
  figure?: FigureType;
  lines: Record<string, LineStyle>;
  fills?: Record<string, FillStyle>;
  histogram?: {
    visible: boolean;
    upColor: string;
    downColor: string;
    opacity: number;
  };
};

export type IndicatorStyle =
  | OscillatorStyle
  | HistogramOnlyStyle
  | ChannelStyle
  | DeMarkerStyle
  | DualLineStyle
  | StcStyle
  | FractalStyle
  | ChaosBandsStyle
  | IchimokuStyle
  | LegacyIndicatorStyle;

export type IndicatorSettingValue = boolean | number | MovingAverageType | EnvelopeSource | IndicatorStyle;

export const STYLE_PALETTE = [
  "#86DA0E",
  "#00FFCC",
  "#F1C40F",
  "#E67E22",
  "#FF0000",
  "#25E154",
  "#FF6C58",
  "#57A1D0",
  "#D35400",
  "#2256FF",
  "#FF00FF",
  "#C0C0C0",
  "#FFFFFF",
  "#2D2D2D",
] as const;

export const STYLED_INDICATOR_IDS = [
  "ma",
  "rsi",
  "macd",
  "bb",
  "stoch",
  "atr",
  "supertrend",
  "cci",
  "williamsr",
  "donchian",
  "sar",
  "adx",
  "momentum",
  "roc",
  "envelopes",
  "bbwidth",
  "osma",
  "bullspower",
  "bearspower",
  "demarker",
  "keltner",
  "ao",
  "ac",
  "alligator",
  "aroon",
  "vortex",
  "stc",
  "fractal",
  "chaosbands",
  "ichimoku",
  "zigzag",
] as const satisfies readonly IndicatorId[];

export type StyledIndicatorId = (typeof STYLED_INDICATOR_IDS)[number];

export function isStyledIndicator(id: IndicatorId | null): id is StyledIndicatorId {
  return id !== null && (STYLED_INDICATOR_IDS as readonly string[]).includes(id);
}

export function snapColorToPalette(color: string, fallback: string = STYLE_PALETTE[0]): string {
  const normalized = color.trim().toLowerCase();
  const hit = STYLE_PALETTE.find((c) => c.toLowerCase() === normalized);
  if (hit) return hit;

  // Legacy Material / Tailwind defaults → nearest palette swatch
  const legacy: Record<string, (typeof STYLE_PALETTE)[number]> = {
    "#4caf50": "#86DA0E",
    "#22c55e": "#86DA0E",
    "#8bc34a": "#86DA0E",
    "#00bcd4": "#00FFCC",
    "#2dd4bf": "#00FFCC",
    "#ffc107": "#F1C40F",
    "#facc15": "#F1C40F",
    "#ff9800": "#E67E22",
    "#f44336": "#FF0000",
    "#ef4444": "#FF0000",
    "#ff5722": "#D35400",
    "#2196f3": "#2256FF",
    "#38bdf8": "#2256FF",
    "#e91e63": "#FF00FF",
    "#a78bfa": "#FF00FF",
    "#c084fc": "#FF00FF",
    "#9e9e9e": "#C0C0C0",
    "#212121": "#2D2D2D",
  };
  return legacy[normalized] ?? fallback;
}

export function defaultLineStyle(color: string, overrides: Partial<LineStyle> = {}): LineStyle {
  const merged = { visible: true, color, width: 1 as StrokeWidth, opacity: 1, ...overrides };
  return { ...merged, color: snapColorToPalette(merged.color) };
}

export function colorWithOpacity(color: string, opacity: number): string {
  const a = Number.isFinite(opacity) ? Math.max(0, Math.min(1, opacity)) : 1;
  if (!color) return `rgba(255, 255, 255, ${a})`;
  if (color.startsWith("rgba")) {
    return color.replace(/rgba?\(([^)]+)\)/, (_, inner) => {
      const parts = inner.split(",").map((s: string) => s.trim());
      return `rgba(${parts[0]}, ${parts[1]}, ${parts[2]}, ${a})`;
    });
  }
  if (color.startsWith("rgb(")) {
    const inner = color.slice(4, -1);
    return `rgba(${inner}, ${a})`;
  }
  let hex = color.replace("#", "");
  if (hex.length === 3)
    hex = hex
      .split("")
      .map((c) => c + c)
      .join("");
  if (hex.length !== 6) return color;
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${a})`;
}

function snapLineStyle(line: LineStyle, fallbackColor: string): LineStyle {
  return { ...line, color: snapColorToPalette(line.color || fallbackColor, fallbackColor) };
}

/** Safe style accessor — prefs may lack nested fields after old saves. */
export function resolveOscillatorStyle(style: OscillatorStyle | undefined, fallbackColor: string): OscillatorStyle {
  const base = DEFAULT_OSC_STYLE(fallbackColor, style?.allowFigureSwitch !== false);
  if (!style) return base;
  return {
    ...base,
    ...style,
    main: snapLineStyle({ ...base.main, ...(style.main ?? {}) }, fallbackColor),
    figure: style.figure === "histogram" ? "histogram" : "line",
    upColor: snapColorToPalette(style.upColor || base.upColor, base.upColor),
    downColor: snapColorToPalette(style.downColor || base.downColor, base.downColor),
  };
}

export function resolveChannelStyle(style: ChannelStyle | undefined, fallback: ChannelStyle): ChannelStyle {
  if (!style) return fallback;
  const bg =
    style.background || fallback.background
      ? {
          visible: true,
          color: "#FF00FF",
          opacity: 0.12,
          ...fallback.background,
          ...style.background,
        }
      : undefined;
  return {
    kind: "channel",
    top: snapLineStyle({ ...fallback.top, ...(style.top ?? {}) }, fallback.top.color),
    middle: snapLineStyle({ ...fallback.middle, ...(style.middle ?? {}) }, fallback.middle.color),
    bottom: snapLineStyle({ ...fallback.bottom, ...(style.bottom ?? {}) }, fallback.bottom.color),
    ...(bg ? { background: { ...bg, color: snapColorToPalette(bg.color, "#FF00FF") } } : {}),
  };
}

export function resolveDeMarkerStyle(style: DeMarkerStyle | undefined): DeMarkerStyle {
  const base = DEFAULT_INDICATOR_SETTINGS.demarker.style;
  if (!style) return base;
  return {
    kind: "demarker",
    main: snapLineStyle({ ...base.main, ...(style.main ?? {}) }, base.main.color),
    levelHigh: snapLineStyle({ ...base.levelHigh, ...(style.levelHigh ?? {}) }, base.levelHigh.color),
    levelLow: snapLineStyle({ ...base.levelLow, ...(style.levelLow ?? {}) }, base.levelLow.color),
    figure: style.figure === "histogram" ? "histogram" : "line",
  };
}

export function resolveHistogramStyle(style: HistogramOnlyStyle | undefined): HistogramOnlyStyle {
  const base = DEFAULT_INDICATOR_SETTINGS.osma.style;
  if (!style) return base;
  return {
    ...base,
    ...style,
    upColor: snapColorToPalette(style.upColor || base.upColor, base.upColor),
    downColor: snapColorToPalette(style.upColor || base.upColor, base.upColor),
  };
}

export function resolveDualLineStyle(style: DualLineStyle | undefined, fallback: DualLineStyle): DualLineStyle {
  if (!style) return fallback;
  return {
    kind: "dualline",
    up: snapLineStyle({ ...fallback.up, ...(style.up ?? {}) }, fallback.up.color),
    down: snapLineStyle({ ...fallback.down, ...(style.down ?? {}) }, fallback.down.color),
  };
}

export function resolveStcStyle(style: StcStyle | undefined, fallback: StcStyle): StcStyle {
  if (!style) return fallback;
  return {
    kind: "stc",
    main: snapLineStyle({ ...fallback.main, ...(style.main ?? {}) }, fallback.main.color),
    levelHigh: snapLineStyle({ ...fallback.levelHigh, ...(style.levelHigh ?? {}) }, fallback.levelHigh.color),
    levelLow: snapLineStyle({ ...fallback.levelLow, ...(style.levelLow ?? {}) }, fallback.levelLow.color),
  };
}

export function resolveFractalStyle(style: FractalStyle | undefined, fallback: FractalStyle): FractalStyle {
  if (!style) return fallback;
  return {
    kind: "fractal",
    up: {
      visible: style.up?.visible ?? fallback.up.visible,
      color: snapColorToPalette(style.up?.color || fallback.up.color, fallback.up.color),
    },
    down: {
      visible: style.down?.visible ?? fallback.down.visible,
      color: snapColorToPalette(style.down?.color || fallback.down.color, fallback.down.color),
    },
  };
}

export function resolveChaosBandsStyle(style: ChaosBandsStyle | undefined, fallback: ChaosBandsStyle): ChaosBandsStyle {
  if (!style) return fallback;
  return {
    kind: "chaosbands",
    up: snapLineStyle({ ...fallback.up, ...(style.up ?? {}) }, fallback.up.color),
    down: snapLineStyle({ ...fallback.down, ...(style.down ?? {}) }, fallback.down.color),
    background: {
      visible: style.background?.visible ?? fallback.background.visible,
      color: snapColorToPalette(style.background?.color || fallback.background.color, fallback.background.color),
      opacity: style.background?.opacity ?? fallback.background.opacity,
    },
  };
}

export function resolveIchimokuStyle(style: IchimokuStyle | undefined, fallback: IchimokuStyle): IchimokuStyle {
  if (!style) return fallback;
  return {
    kind: "ichimoku",
    conversion: snapLineStyle({ ...fallback.conversion, ...(style.conversion ?? {}) }, fallback.conversion.color),
    base: snapLineStyle({ ...fallback.base, ...(style.base ?? {}) }, fallback.base.color),
    spanA: snapLineStyle({ ...fallback.spanA, ...(style.spanA ?? {}) }, fallback.spanA.color),
    spanB: snapLineStyle({ ...fallback.spanB, ...(style.spanB ?? {}) }, fallback.spanB.color),
    lagging: snapLineStyle({ ...fallback.lagging, ...(style.lagging ?? {}) }, fallback.lagging.color),
    bullishCloud: {
      ...fallback.bullishCloud,
      ...(style.bullishCloud ?? {}),
      color: snapColorToPalette(style.bullishCloud?.color || fallback.bullishCloud.color, fallback.bullishCloud.color),
    },
    bearishCloud: {
      ...fallback.bearishCloud,
      ...(style.bearishCloud ?? {}),
      color: snapColorToPalette(style.bearishCloud?.color || fallback.bearishCloud.color, fallback.bearishCloud.color),
    },
  };
}

export function chipColorFromStyle(style: IndicatorStyle | undefined, fallback: string): string {
  if (!style) return fallback;
  if (style.kind === "oscillator" || style.kind === "demarker" || style.kind === "stc") return style.main.color;
  if (style.kind === "channel") return style.top.color;
  if (style.kind === "dualline" || style.kind === "fractal" || style.kind === "chaosbands") return style.up.color;
  if (style.kind === "ichimoku") return style.conversion.color;
  if (style.kind === "legacy") return Object.values(style.lines)[0]?.color ?? fallback;
  return style.upColor;
}

const DEFAULT_OSC_STYLE = (color: string, allowFigureSwitch = true): OscillatorStyle => ({
  kind: "oscillator",
  main: defaultLineStyle(color),
  figure: "line",
  upColor: "#86DA0E",
  downColor: "#FF0000",
  allowFigureSwitch,
});

const legacyStyle = (
  lines: Record<string, string>,
  options: Pick<LegacyIndicatorStyle, "figure" | "fills" | "histogram"> = {},
): LegacyIndicatorStyle => ({
  kind: "legacy",
  lines: Object.fromEntries(Object.entries(lines).map(([key, color]) => [key, defaultLineStyle(color)])),
  ...options,
});

export type MaSettings = {
  period: number;
  period2: number;
  period3: number;
  count: number;
  type: MovingAverageType;
  type2: MovingAverageType;
  type3: MovingAverageType;
  enabled1?: boolean;
  enabled2?: boolean;
  enabled3?: boolean;
  style: LegacyIndicatorStyle;
};
export type RsiSettings = { period: number; overbought: number; oversold: number; style: LegacyIndicatorStyle };
export type MacdSettings = { fast: number; slow: number; signal: number; style: LegacyIndicatorStyle };
export type BbSettings = { period: number; multiplier: number; style: LegacyIndicatorStyle };
export type StochSettings = {
  kPeriod: number;
  dPeriod: number;
  overbought: number;
  oversold: number;
  style: LegacyIndicatorStyle;
};
export type AtrSettings = { period: number; style: LegacyIndicatorStyle };
export type SuperTrendSettings = { period: number; multiplier: number; style: LegacyIndicatorStyle };
export type CciSettings = { period: number; overbought: number; oversold: number; style: LegacyIndicatorStyle };
export type WilliamsRSettings = { period: number; overbought: number; oversold: number; style: LegacyIndicatorStyle };
export type DonchianSettings = { period: number; style: LegacyIndicatorStyle };
export type SarSettings = { step: number; max: number; style: LegacyIndicatorStyle };
export type AdxSettings = { period: number; threshold: number; style: LegacyIndicatorStyle };
export type MomentumSettings = { period: number; style: OscillatorStyle };
export type RocSettings = { period: number; style: OscillatorStyle };
export type EnvelopesSettings = {
  period: number;
  deviation: number;
  /** MA method — Figma label "Moving Average". */
  movingAverage: MovingAverageType;
  source: EnvelopeSource;
  style: ChannelStyle;
  /** @deprecated migrated to movingAverage */
  type?: MovingAverageType;
};
export type BbWidthSettings = { period: number; multiplier: number; style: OscillatorStyle };
export type OsmaSettings = { fast: number; slow: number; signal: number; style: HistogramOnlyStyle };
export type BullsPowerSettings = { period: number; style: OscillatorStyle };
export type BearsPowerSettings = { period: number; style: OscillatorStyle };
export type DeMarkerSettings = { period: number; overbought: number; oversold: number; style: DeMarkerStyle };
export type KeltnerSettings = { emaPeriod: number; atrPeriod: number; multiplier: number; style: ChannelStyle };
export type AwesomeSettings = { periodShort: number; periodLong: number; style: OscillatorStyle };
export type AcceleratorSettings = {
  aoShortPeriod: number;
  aoLongPeriod: number;
  acSmaPeriod: number;
  style: OscillatorStyle;
};
export type AlligatorSettings = {
  jawsPeriod: number;
  jawsShift: number;
  teethPeriod: number;
  teethShift: number;
  lipsPeriod: number;
  lipsShift: number;
  style: ChannelStyle;
};
export type AroonSettings = { period: number; style: DualLineStyle };
export type VortexSettings = { period: number; style: DualLineStyle };
export type StcSettings = {
  fastLength: number;
  slowLength: number;
  cycleLength: number;
  dMacdLength: number;
  dPfLength: number;
  overbought: number;
  oversold: number;
  style: StcStyle;
};
export type FractalSettings = { period: number; style: FractalStyle };
export type ChaosBandsSettings = { period: number; style: ChaosBandsStyle };
export type IchimokuSettings = {
  conversionPeriod: number;
  basePeriod: number;
  spanBPeriod: number;
  displacement: number;
  style: IchimokuStyle;
};
export type ZigZagSettings = { deviation: number; depth: number; backstep: number; style: OscillatorStyle };

export type AllIndicatorSettings = {
  ma: MaSettings;
  rsi: RsiSettings;
  macd: MacdSettings;
  bb: BbSettings;
  stoch: StochSettings;
  atr: AtrSettings;
  supertrend: SuperTrendSettings;
  cci: CciSettings;
  williamsr: WilliamsRSettings;
  donchian: DonchianSettings;
  sar: SarSettings;
  adx: AdxSettings;
  momentum: MomentumSettings;
  roc: RocSettings;
  envelopes: EnvelopesSettings;
  bbwidth: BbWidthSettings;
  osma: OsmaSettings;
  bullspower: BullsPowerSettings;
  bearspower: BearsPowerSettings;
  demarker: DeMarkerSettings;
  keltner: KeltnerSettings;
  ao: AwesomeSettings;
  ac: AcceleratorSettings;
  alligator: AlligatorSettings;
  aroon: AroonSettings;
  vortex: VortexSettings;
  stc: StcSettings;
  fractal: FractalSettings;
  chaosbands: ChaosBandsSettings;
  ichimoku: IchimokuSettings;
  zigzag: ZigZagSettings;
};

export const DEFAULT_INDICATOR_SETTINGS: AllIndicatorSettings = {
  ma: {
    period: 20,
    period2: 50,
    period3: 200,
    count: 1,
    type: "SMA",
    type2: "SMA",
    type3: "SMA",
    enabled1: true,
    enabled2: false,
    enabled3: false,
    style: legacyStyle({ period1: "#2256FF", period2: "#E67E22", period3: "#86DA0E" }),
  },
  rsi: {
    period: 14,
    overbought: 70,
    oversold: 30,
    style: legacyStyle({ main: "#FF00FF", overbought: "#FF0000", oversold: "#86DA0E" }),
  },
  macd: {
    fast: 12,
    slow: 26,
    signal: 9,
    style: legacyStyle(
      { macd: "#57A1D0", signal: "#E67E22" },
      { histogram: { visible: true, upColor: "#86DA0E", downColor: "#FF0000", opacity: 0.5 } },
    ),
  },
  bb: {
    period: 20,
    multiplier: 2,
    style: legacyStyle(
      { top: "#86DA0E", middle: "#00FFCC", bottom: "#F1C40F" },
      { fills: { background: { visible: true, color: "#86DA0E", opacity: 0.12 } } },
    ),
  },
  stoch: {
    kPeriod: 14,
    dPeriod: 3,
    overbought: 80,
    oversold: 20,
    style: legacyStyle({ k: "#57A1D0", d: "#E67E22", overbought: "#FF0000", oversold: "#86DA0E" }),
  },
  atr: { period: 14, style: legacyStyle({ main: "#F1C40F" }) },
  supertrend: { period: 10, multiplier: 3, style: legacyStyle({ up: "#86DA0E", down: "#FF0000" }) },
  cci: {
    period: 20,
    overbought: 100,
    oversold: -100,
    style: legacyStyle(
      { main: "#FF00FF", overbought: "#FF0000", oversold: "#86DA0E" },
      { fills: { background: { visible: true, color: "#FF00FF", opacity: 0.08 } } },
    ),
  },
  williamsr: {
    period: 14,
    overbought: -20,
    oversold: -80,
    style: legacyStyle({ main: "#25E154", overbought: "#FF0000", oversold: "#86DA0E" }, { figure: "line" }),
  },
  donchian: {
    period: 20,
    style: legacyStyle(
      { middle: "#57A1D0", top: "#57A1D0", bottom: "#57A1D0" },
      { fills: { background: { visible: true, color: "#57A1D0", opacity: 0.1 } } },
    ),
  },
  sar: { step: 0.02, max: 0.2, style: legacyStyle({ main: "#86DA0E" }) },
  adx: { period: 14, threshold: 25, style: legacyStyle({ adx: "#F1C40F", plusDI: "#86DA0E", minusDI: "#FF0000" }) },
  momentum: { period: 10, style: DEFAULT_OSC_STYLE("#2256FF", true) },
  roc: { period: 10, style: DEFAULT_OSC_STYLE("#F1C40F", false) },
  envelopes: {
    period: 14,
    deviation: 0.1,
    movingAverage: "SMA",
    source: "close",
    style: {
      kind: "channel",
      top: defaultLineStyle("#F1C40F"),
      middle: defaultLineStyle("#00FFCC"),
      bottom: defaultLineStyle("#86DA0E"),
      background: { visible: true, color: "#FF00FF", opacity: 0.12 },
    },
  },
  bbwidth: { period: 20, multiplier: 2, style: DEFAULT_OSC_STYLE("#FF00FF", false) },
  osma: {
    fast: 12,
    slow: 26,
    signal: 9,
    style: { kind: "histogram", upColor: "#86DA0E", downColor: "#86DA0E", opacity: 0.5, width: 1 },
  },
  bullspower: {
    period: 13,
    style: { ...DEFAULT_OSC_STYLE("#F1C40F", true), figure: "histogram" },
  },
  bearspower: {
    period: 13,
    style: { ...DEFAULT_OSC_STYLE("#F1C40F", true), figure: "histogram" },
  },
  demarker: {
    period: 14,
    overbought: 70,
    oversold: 30,
    style: {
      kind: "demarker",
      main: defaultLineStyle("#00FFCC"),
      levelHigh: defaultLineStyle("#86DA0E"),
      levelLow: defaultLineStyle("#FF0000"),
      figure: "line",
    },
  },
  keltner: {
    emaPeriod: 20,
    atrPeriod: 10,
    multiplier: 2,
    style: {
      kind: "channel",
      top: defaultLineStyle("#00FFCC"),
      middle: defaultLineStyle("#00FFCC", { opacity: 0.55 }),
      bottom: defaultLineStyle("#00FFCC"),
      background: { visible: true, color: "#00FFCC", opacity: 0.12 },
    },
  },
  ao: {
    periodShort: 5,
    periodLong: 34,
    style: { ...DEFAULT_OSC_STYLE("#86DA0E", true), figure: "histogram", downColor: "#FF0000" },
  },
  ac: {
    aoShortPeriod: 5,
    aoLongPeriod: 34,
    acSmaPeriod: 5,
    style: { ...DEFAULT_OSC_STYLE("#86DA0E", true), figure: "histogram", downColor: "#FF0000" },
  },
  alligator: {
    jawsPeriod: 13,
    jawsShift: 8,
    teethPeriod: 8,
    teethShift: 5,
    lipsPeriod: 5,
    lipsShift: 3,
    style: {
      kind: "channel",
      top: defaultLineStyle("#00FFCC"),
      middle: defaultLineStyle("#E67E22"),
      bottom: defaultLineStyle("#2256FF"),
    },
  },
  aroon: {
    period: 25,
    style: {
      kind: "dualline",
      up: defaultLineStyle("#2256FF"),
      down: defaultLineStyle("#FF0000"),
    },
  },
  vortex: {
    period: 14,
    style: {
      kind: "dualline",
      up: defaultLineStyle("#86DA0E"),
      down: defaultLineStyle("#FF0000"),
    },
  },
  stc: {
    fastLength: 23,
    slowLength: 50,
    cycleLength: 10,
    dMacdLength: 3,
    dPfLength: 3,
    overbought: 75,
    oversold: 25,
    style: {
      kind: "stc",
      main: defaultLineStyle("#2256FF"),
      levelHigh: defaultLineStyle("#00FFCC"),
      levelLow: defaultLineStyle("#F1C40F"),
    },
  },
  fractal: {
    period: 5,
    style: {
      kind: "fractal",
      up: { visible: true, color: snapColorToPalette("#86DA0E") },
      down: { visible: true, color: snapColorToPalette("#FF0000") },
    },
  },
  chaosbands: {
    period: 5,
    style: {
      kind: "chaosbands",
      up: defaultLineStyle("#25E154"),
      down: defaultLineStyle("#FF6C58"),
      background: { visible: true, color: snapColorToPalette("#57A1D0"), opacity: 0.15 },
    },
  },
  ichimoku: {
    conversionPeriod: 9,
    basePeriod: 26,
    spanBPeriod: 52,
    displacement: 26,
    style: {
      kind: "ichimoku",
      conversion: defaultLineStyle("#2256FF"),
      base: defaultLineStyle("#FF0000"),
      spanA: defaultLineStyle("#86DA0E"),
      spanB: defaultLineStyle("#FF00FF"),
      lagging: defaultLineStyle("#C0C0C0", { opacity: 0.75 }),
      bullishCloud: { visible: true, color: "#86DA0E", opacity: 0.13 },
      bearishCloud: { visible: true, color: "#FF0000", opacity: 0.13 },
    },
  },
  zigzag: {
    deviation: 2,
    depth: 12,
    backstep: 5,
    style: DEFAULT_OSC_STYLE("#E67E22", false),
  },
};

// ─── Indicator metadata list ──────────────────────────────────────────────────

export const ALL_INDICATORS_META: { id: IndicatorId; label: string }[] = [
  { id: "ma", label: "Moving Average" },
  { id: "rsi", label: "RSI" },
  { id: "macd", label: "MACD" },
  { id: "bb", label: "Bollinger Bands" },
  { id: "stoch", label: "Stochastic Oscillator" },
  { id: "atr", label: "Average True Range (ATR)" },
  { id: "supertrend", label: "SuperTrend" },
  { id: "cci", label: "CCI" },
  { id: "williamsr", label: "Williams %R" },
  { id: "donchian", label: "Donchian Channels" },
  { id: "sar", label: "Parabolic SAR" },
  { id: "adx", label: "ADX" },
  { id: "momentum", label: "Momentum" },
  { id: "roc", label: "Rate of Change" },
  { id: "envelopes", label: "Envelopes" },
  { id: "bbwidth", label: "Bollinger Bands Width" },
  { id: "osma", label: "OsMA" },
  { id: "bullspower", label: "Bulls Power" },
  { id: "bearspower", label: "Bears Power" },
  { id: "demarker", label: "DeMarker" },
  { id: "keltner", label: "Keltner Channel" },
  { id: "ao", label: "Awesome Oscillator" },
  { id: "ac", label: "Accelerator Oscillator" },
  { id: "alligator", label: "Alligator" },
  { id: "aroon", label: "Aroon" },
  { id: "vortex", label: "Vortex" },
  { id: "stc", label: "Schaff Trend Cycle" },
  { id: "fractal", label: "Fractal" },
  { id: "chaosbands", label: "Fractal Chaos Bands" },
  { id: "ichimoku", label: "Ichimoku Kinko Hyo" },
  { id: "zigzag", label: "ZigZag" },
];

// ─── Settings field definitions (used in IndicatorSettingsModal) ──────────────

export interface SettingField {
  key: string;
  label: string;
  type: "int" | "float";
  min?: number;
  max?: number;
  step?: number;
}

export const INDICATOR_SETTINGS_FIELDS: Record<IndicatorId, SettingField[]> = {
  ma: [{ key: "period", label: "Period", type: "int", min: 1, max: 500 }],
  rsi: [
    { key: "period", label: "Period", type: "int", min: 2, max: 500 },
    { key: "overbought", label: "Overbought", type: "int", min: 50, max: 99 },
    { key: "oversold", label: "Oversold", type: "int", min: 1, max: 49 },
  ],
  macd: [
    { key: "fast", label: "Fast Period", type: "int", min: 1, max: 200 },
    { key: "slow", label: "Slow Period", type: "int", min: 1, max: 200 },
    { key: "signal", label: "Signal Period", type: "int", min: 1, max: 200 },
  ],
  bb: [
    { key: "period", label: "Period", type: "int", min: 1, max: 500 },
    { key: "multiplier", label: "Multiplier", type: "float", min: 0.1, max: 10, step: 0.1 },
  ],
  stoch: [
    { key: "kPeriod", label: "%K Period", type: "int", min: 1, max: 200 },
    { key: "dPeriod", label: "%D Period", type: "int", min: 1, max: 200 },
    { key: "overbought", label: "Overbought", type: "int", min: 50, max: 99 },
    { key: "oversold", label: "Oversold", type: "int", min: 1, max: 49 },
  ],
  atr: [{ key: "period", label: "Period", type: "int", min: 1, max: 500 }],
  supertrend: [
    { key: "period", label: "ATR Period", type: "int", min: 1, max: 200 },
    { key: "multiplier", label: "Multiplier", type: "float", min: 0.1, max: 20, step: 0.1 },
  ],
  cci: [
    { key: "period", label: "Period", type: "int", min: 1 },
    { key: "overbought", label: "Overbought", type: "int" },
    { key: "oversold", label: "Oversold", type: "int" },
  ],
  williamsr: [
    { key: "period", label: "Period", type: "int", min: 1, max: 500 },
    { key: "overbought", label: "Overbought", type: "int", min: -99, max: 0 },
    { key: "oversold", label: "Oversold", type: "int", min: -99, max: 0 },
  ],
  donchian: [{ key: "period", label: "Period", type: "int", min: 1, max: 500 }],
  sar: [
    { key: "step", label: "Step", type: "float", min: 0.001, max: 0.5, step: 0.001 },
    { key: "max", label: "Max Step", type: "float", min: 0.01, max: 0.5, step: 0.01 },
  ],
  adx: [
    { key: "period", label: "Period", type: "int", min: 1, max: 500 },
    { key: "threshold", label: "Threshold", type: "int", min: 1, max: 100 },
  ],
  momentum: [{ key: "period", label: "Period", type: "int", min: 1, max: 500 }],
  roc: [{ key: "period", label: "Period", type: "int", min: 1, max: 500 }],
  envelopes: [
    { key: "period", label: "Period", type: "int", min: 1, max: 500 },
    { key: "deviation", label: "Deviation", type: "float", min: 0.01, max: 50, step: 0.01 },
  ],
  bbwidth: [
    { key: "period", label: "Period", type: "int", min: 1, max: 500 },
    { key: "multiplier", label: "Multiplier", type: "float", min: 0.1, max: 10, step: 0.1 },
  ],
  osma: [
    { key: "fast", label: "Fast Period", type: "int", min: 1, max: 200 },
    { key: "slow", label: "Slow Period", type: "int", min: 1, max: 200 },
    { key: "signal", label: "Signal Period", type: "int", min: 1, max: 200 },
  ],
  bullspower: [{ key: "period", label: "Period", type: "int", min: 1, max: 500 }],
  bearspower: [{ key: "period", label: "Period", type: "int", min: 1, max: 500 }],
  demarker: [
    { key: "period", label: "Period", type: "int", min: 1, max: 500 },
    { key: "overbought", label: "Overbought", type: "int", min: 1, max: 99 },
    { key: "oversold", label: "Oversold", type: "int", min: 1, max: 99 },
  ],
  keltner: [
    { key: "emaPeriod", label: "EMA Period", type: "int", min: 1, max: 500 },
    { key: "atrPeriod", label: "ATR Period", type: "int", min: 1, max: 500 },
    { key: "multiplier", label: "Multiplier", type: "float", min: 0.1, max: 20, step: 0.1 },
  ],
  ao: [
    { key: "periodShort", label: "Period Short", type: "int", min: 1, max: 500 },
    { key: "periodLong", label: "Period Long", type: "int", min: 1, max: 500 },
  ],
  ac: [
    { key: "aoShortPeriod", label: "AO Short Period", type: "int", min: 1, max: 500 },
    { key: "aoLongPeriod", label: "AO Long Period", type: "int", min: 1, max: 500 },
    { key: "acSmaPeriod", label: "AC SMA Period", type: "int", min: 1, max: 500 },
  ],
  alligator: [
    { key: "jawsPeriod", label: "Jaws Period", type: "int", min: 1, max: 500 },
    { key: "jawsShift", label: "Jaws Shift", type: "int", min: 0, max: 500 },
    { key: "teethPeriod", label: "Teeth Period", type: "int", min: 1, max: 500 },
    { key: "teethShift", label: "Teeth Shift", type: "int", min: 0, max: 500 },
    { key: "lipsPeriod", label: "Lips Period", type: "int", min: 1, max: 500 },
    { key: "lipsShift", label: "Lips Shift", type: "int", min: 0, max: 500 },
  ],
  aroon: [{ key: "period", label: "Period", type: "int", min: 1, max: 500 }],
  vortex: [{ key: "period", label: "Period", type: "int", min: 1, max: 500 }],
  stc: [
    { key: "fastLength", label: "Fast Length", type: "int", min: 1, max: 500 },
    { key: "slowLength", label: "Slow Length", type: "int", min: 1, max: 500 },
    { key: "cycleLength", label: "Cycle Length", type: "int", min: 1, max: 500 },
    { key: "dMacdLength", label: "%D (MACD) Length", type: "int", min: 1, max: 500 },
    { key: "dPfLength", label: "%D (PF) Length", type: "int", min: 1, max: 500 },
  ],
  fractal: [{ key: "period", label: "Period", type: "int", min: 3, max: 50 }],
  chaosbands: [{ key: "period", label: "Period", type: "int", min: 3, max: 50 }],
  ichimoku: [
    { key: "conversionPeriod", label: "Conversion Period", type: "int", min: 1, max: 500 },
    { key: "basePeriod", label: "Base Period", type: "int", min: 1, max: 500 },
    { key: "spanBPeriod", label: "Leading Span B Period", type: "int", min: 1, max: 500 },
    { key: "displacement", label: "Displacement", type: "int", min: 0, max: 500 },
  ],
  zigzag: [
    { key: "deviation", label: "Deviation", type: "int", min: 1, max: 100_000 },
    { key: "depth", label: "Depth", type: "int", min: 1, max: 500 },
    { key: "backstep", label: "Backstep", type: "int", min: 2, max: 500 },
  ],
};

// ─── Indicator data point types ───────────────────────────────────────────────

export interface BbPoint {
  time: number;
  upper: number;
  middle: number;
  lower: number;
}
export interface StochPoint {
  time: number;
  k: number;
  d: number;
}
export interface AtrPoint {
  time: number;
  value: number;
}
export interface SuperTrendPoint {
  time: number;
  value: number;
  trend: 1 | -1;
}
export interface CciPoint {
  time: number;
  value: number;
}
export interface WilliamsRPoint {
  time: number;
  value: number;
}
export interface DonchianPoint {
  time: number;
  upper: number;
  middle: number;
  lower: number;
}
export interface SarPoint {
  time: number;
  value: number;
  trend: 1 | -1;
}
export interface AdxPoint {
  time: number;
  adx: number;
  diPlus: number;
  diMinus: number;
}
export interface EnvelopesPoint {
  time: number;
  upper: number;
  middle: number;
  lower: number;
}
export interface KeltnerPoint {
  time: number;
  upper: number;
  middle: number;
  lower: number;
}
export interface AlligatorLinePoint {
  time: number;
  value: number;
}
export interface FractalPoint {
  time: number;
  up?: number;
  down?: number;
}
export interface ChaosBandsPoint {
  time: number;
  upper?: number;
  lower?: number;
}
export interface IchimokuPoint {
  time: number;
  tenkan?: number;
  kijun?: number;
  senkouA?: number;
  senkouB?: number;
  chikou?: number;
}
export interface ZigZagPoint {
  time: number;
  value: number;
  direction: "up" | "down";
  startsSegment?: boolean;
}

export interface OverlayIndicatorData {
  bb?: BbPoint[];
  supertrend?: SuperTrendPoint[];
  donchian?: DonchianPoint[];
  sar?: SarPoint[];
  envelopes?: EnvelopesPoint[];
  keltner?: KeltnerPoint[];
  alligator?: { jaws: AlligatorLinePoint[]; teeth: AlligatorLinePoint[]; lips: AlligatorLinePoint[] };
  fractal?: FractalPoint[];
  chaosbands?: ChaosBandsPoint[];
  ichimoku?: IchimokuPoint[];
  zigzag?: ZigZagPoint[];
  maStyle?: LegacyIndicatorStyle;
  bbStyle?: LegacyIndicatorStyle;
  supertrendStyle?: LegacyIndicatorStyle;
  donchianStyle?: LegacyIndicatorStyle;
  sarStyle?: LegacyIndicatorStyle;
  envelopesStyle?: ChannelStyle;
  keltnerStyle?: ChannelStyle;
  alligatorStyle?: ChannelStyle;
  fractalStyle?: FractalStyle;
  chaosbandsStyle?: ChaosBandsStyle;
  ichimokuStyle?: IchimokuStyle;
  zigzagStyle?: OscillatorStyle;
}
